import { useMemo } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { productRecipesMissingLaborRate } from "@/lib/manufacturing/laborCompanyRateGaps";
import type { MfgLaborRateItem } from "@/lib/manufacturing/laborBillingTypes";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function ManufacturingLaborCompanyRateGaps({
  recipes,
  rates,
  search,
  readOnly,
  busy,
  onAdd,
}: {
  recipes: readonly MfgRecipeSummary[];
  rates: readonly MfgLaborRateItem[];
  search: string;
  readOnly: boolean;
  busy: boolean;
  onAdd: (recipe: MfgRecipeSummary) => void;
}) {
  const holes = useMemo(() => productRecipesMissingLaborRate(recipes, rates), [rates, recipes]);
  const missing = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return holes;
    return holes.filter((recipe) => `${recipe.code} ${recipe.name}`.toLowerCase().includes(q));
  }, [holes, search]);
  const page = usePagination(missing, LABOR_PAY_DEFAULT_PAGE_SIZE);
  const holesExist = holes.length > 0;

  if (!holesExist) return null;

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-900/60 dark:bg-amber-950/20">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Needs a company rate</h4>
      <p className="mb-2 text-xs text-gray-600 dark:text-zinc-400">
        These product SKUs exist in Models and have no active company labor rate.
      </p>
      {missing.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No product SKUs match that search.</p>
      ) : (
        <ul className="divide-y divide-amber-200/80 dark:divide-amber-900/40">
          {page.paginatedItems.map((recipe) => (
            <li key={recipe.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 dark:text-zinc-100">{recipe.name}</div>
                <code className="text-xs text-gray-500 dark:text-zinc-400">{recipe.code}</code>
              </div>
              {readOnly ? null : (
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={busy}
                  onClick={() => onAdd(recipe)}
                >
                  Add rate
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <PaginationBar
        pageIndex={page.pageIndex}
        pageCount={page.pageCount}
        pageSize={page.pageSize}
        totalCount={page.totalCount}
        firstShown={page.firstShown}
        lastShown={page.lastShown}
        onPageChange={page.setPageIndex}
        onPageSizeChange={page.setPageSize}
        pageSizeOptions={LABOR_PAY_PAGE_SIZES}
        noun="SKUs"
      />
    </div>
  );
}
